// 261023 E3-P3b1 (Bước 4) — thẻ «Máy đã tự sửa»: hàm THUẦN (jest). KHÔNG import antd / react.
//
// Luật cứng:
//  - Mọi chỗ đọc `repair` / `beforeScenes` / `changedScenes` đọc từ biên lai LÚC SINH = `qualityReview.generatedCritic ?? qualityReview.critic` (lát 3b2 D3: chấm lại sẽ thay `critic`
//    bằng biên lai không có `repair`). Chip giám khảo hiện hành vẫn đọc `critic` ở `criticReceiptView.ts` — không đổi.
//  - Trạng thái «đang giữ / đã hoàn tác / bị đè» do BE tính (`WorkbenchEpisode.autoFix`); FE KHÔNG tự suy từ dòng phiên bản.
//  - Tập không có `autoFix` mà biên lai nói REPAIRED + nhận bản sửa = tập viết trước lát này (Q8): chỉ biên nhận + dòng xám, không nút.
import type { C2Finding, EpisodeScriptRecord, QualityReviewCritic, ScriptBeat, ScriptScene } from '../../../types/adaptation';
import type { WorkbenchEpisode } from '../../../types/adaptationWorkbench';
import { CRITIC_STATUSES, sceneLabel } from './criticReceiptView';

// ─── Biên lai lúc sinh ────────────────────────────────────────────────────

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const validCritic = (c: unknown): c is QualityReviewCritic => isObj(c) && CRITIC_STATUSES.includes(c.status as never);

/** `generatedCritic ?? critic` của biên lai v2 (hoặc null). `generatedCritic` hỏng ⇒ rơi về `critic`. */
export const repairReceiptOf = (rec: Pick<EpisodeScriptRecord, 'script'> | null | undefined): QualityReviewCritic | null => {
  const qr: unknown = rec?.script?.qualityReview;
  if (!isObj(qr) || qr.version !== 2) return null;
  if (validCritic(qr.generatedCritic)) return qr.generatedCritic;
  return validCritic(qr.critic) ? qr.critic : null;
};

const findings = (x: unknown): C2Finding[] => (Array.isArray(x) ? x : []).filter((f): f is C2Finding => isObj(f) && typeof f.severity === 'string');
const criticalCount = (list: C2Finding[]): number => list.filter((f) => f.severity === 'CRITICAL').length;
const isScene = (x: unknown): x is ScriptScene => isObj(x) && Array.isArray((x as { beats?: unknown }).beats ?? []) && typeof (x as { locationId?: unknown }).locationId === 'string';

/** CRITICAL của lượt chấm ĐẦU (hợp lệ ∪ cấp tập) — số lỗi nặng máy phải sửa; cũng là số lỗi trở lại khi hoàn tác về bản trước. */
const firstPassCritical = (c: QualityReviewCritic): number => criticalCount([...findings(c.findingsBefore), ...findings(c.unscoped)]);
/** CRITICAL còn sau lượt chấm lại; `null` khi biên lai không có kết quả chấm lại. */
const afterCritical = (c: QualityReviewCritic): number | null => {
  const r = isObj(c.repair) ? c.repair : null;
  if (!r || !Array.isArray(r.findingsAfter)) return null;
  return criticalCount([...findings(r.findingsAfter), ...findings(r.unscopedAfter)]);
};
const acceptedRepair = (c: QualityReviewCritic | null): c is QualityReviewCritic => !!c && c.status === 'REPAIRED' && isObj(c.repair) && c.repair.accepted === true;

// ─── Thẻ ──────────────────────────────────────────────────────────────────

export type AutoFixCardState = 'current' | 'undone' | 'superseded' | 'legacy';
export interface AutoFixStep { key: 'judge1' | 'repair' | 'judge2'; text: string; tone: 'bad' | 'ok' | 'warn' | 'muted' }
export interface AutoFixScenePair {
  scenePlanId: string;
  before: ScriptScene | null;
  after: ScriptScene | null;
  beforeTitle: string;
  afterTitle: string;
}
export interface AutoFixCard {
  ep: number;
  state: AutoFixCardState;
  preVersionNo: number | null;
  postVersionNo: number | null;
  /** Số CRITICAL đã hết sau chấm lại (≥ 0); `null` khi không đọc được. */
  fixedCount: number | null;
  summary: string;
  steps: AutoFixStep[];
  scenes: Array<{ scenePlanId: string; label: string }>;
  /** Cặp cảnh trước/sau — rỗng khi không được xem trước/sau. */
  pairs: AutoFixScenePair[];
  canDiff: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undoVersionId: string | null;
  redoVersionId: string | null;
  undoLabel: string | null;
  redoLabel: string | null;
  greyNote: string | null;
  orangeNote: string | null;
}

export const LEGACY_NOTE = 'Tập viết trước khi có tính năng lưu bản trước sửa — không xem trước/sau được';
export const SUPERSEDED_NOTE = 'Tập đã được sửa sau lần máy sửa — mở Phiên bản nếu cần quay lại';
export const UNDONE_NOTE = 'Bạn đã hoàn tác bản máy sửa — máy bỏ qua tập này';

/** 3e: nhánh chuỗi sửa cả MAJOR ⇒ «số lỗi» ở chuỗi bước đếm CRITICAL + MAJOR (đếm riêng CRITICAL sẽ ra «0 lỗi» cho tập chỉ sửa MAJOR). Biên lai cũ ⇒ y như trước (chỉ CRITICAL). */
const stepCount = (c: QualityReviewCritic, list: C2Finding[]): number => (c.chain ? list.filter((f) => f.severity === 'CRITICAL' || f.severity === 'MAJOR').length : criticalCount(list));
const chainRoundsRun = (c: QualityReviewCritic): number => (isObj(c.chain) && Array.isArray(c.chain.rounds) ? c.chain.rounds.filter((r) => isObj(r) && typeof r.round === 'number').length : 0);

const stepsOf = (c: QualityReviewCritic): AutoFixStep[] => {
  if (!isObj(c.repair)) return [];
  const before = stepCount(c, [...findings(c.findingsBefore), ...findings(c.unscoped)]);
  const after = Array.isArray(c.repair.findingsAfter) ? stepCount(c, [...findings(c.repair.findingsAfter), ...findings(c.repair.unscopedAfter)]) : null;
  const judge2: AutoFixStep = after === null ? { key: 'judge2', text: 'Chấm lại: không đọc được kết quả', tone: 'muted' }
    : after === 0 ? { key: 'judge2', text: 'Chấm lại: Đạt', tone: 'ok' }
      : { key: 'judge2', text: `Chấm lại: còn ${after} lỗi${c.chain ? '' : ' nặng'}`, tone: 'warn' };
  const repairs: AutoFixStep[] = chainRoundsRun(c) > 0
    ? (c.chain!.rounds as Array<{ round: number; accepted: boolean }>).filter((r) => isObj(r) && typeof r.round === 'number')
      .map((r) => ({ key: 'repair' as const, text: `Sửa vòng ${r.round}${r.accepted ? '' : ' (bị bỏ)'}`, tone: r.accepted ? 'muted' as const : 'warn' as const }))
    : [{ key: 'repair', text: 'Sửa vòng 1', tone: 'muted' }];
  return [{ key: 'judge1', text: `Chấm ✗ ${before} lỗi`, tone: 'bad' }, ...repairs, judge2];
};

/**
 * Thẻ «Máy đã tự sửa» của một tập, hoặc `null` (không có gì để nói). Có `ep.autoFix` ⇒ theo trạng thái BE; không có mà biên lai (lúc sinh) nói REPAIRED + nhận ⇒ `legacy` (Q8).
 * Chỉ `current` được xem trước/sau và hoàn tác; chỉ `undone` có «Dùng lại».
 */
export const autoFixCardOf = (
  ep: Pick<WorkbenchEpisode, 'ep' | 'written' | 'autoFix'>, script: EpisodeScriptRecord | null | undefined,
): AutoFixCard | null => {
  if (ep.written !== 'draft' || !script?.script) return null;
  const receipt = repairReceiptOf(script);
  const fix = ep.autoFix;
  if (!fix && !acceptedRepair(receipt)) return null;
  const state: AutoFixCardState = fix ? fix.state : 'legacy';
  const pre = fix?.preVersionNo ?? null;
  const post = fix?.postVersionNo ?? null;
  const scenesNow = script.script.scenes;

  const changed = isObj(receipt?.repair) && Array.isArray(receipt!.repair!.changedScenes) ? receipt!.repair!.changedScenes.filter((x): x is string => typeof x === 'string') : [];
  const scenes = changed.map((id) => ({ scenePlanId: id, label: sceneLabel(id, scenesNow) }));

  const beforeScenes = (isObj(receipt?.repair) && Array.isArray(receipt!.repair!.beforeScenes) ? receipt!.repair!.beforeScenes : []).filter(isScene);
  const diffAllowed = state === 'current';
  const pairs: AutoFixScenePair[] = !diffAllowed ? [] : beforeScenes.map((b) => {
    const id = b.scenePlanId ?? '';
    const label = id ? sceneLabel(id, scenesNow) : 'Cảnh';
    return {
      scenePlanId: id, before: b, after: scenesNow.find((s) => s.scenePlanId === id) ?? null,
      beforeTitle: `Trước · v${pre ?? '?'} · ${label}`, afterTitle: `Sau · v${post ?? '?'} · ${label}`,
    };
  });

  const before = receipt ? firstPassCritical(receipt) : null;
  const after = receipt ? afterCritical(receipt) : null;
  const fixedCount = state === 'undone' || before === null ? null : after === null ? before : Math.max(before - after, 0);
  let summary: string;
  if (state === 'undone') summary = `Bạn đã hoàn tác bản máy sửa — ${before ?? 0} lỗi nặng của lượt chấm đầu đã trở lại`;
  else if (receipt && chainRoundsRun(receipt) > 0) summary = `Máy đã sửa ${changed.length} cảnh · ${chainRoundsRun(receipt)} vòng · bước ⑤ Kịch bản`;   // 3e: nhánh chuỗi — đếm vòng/cảnh thật, không «0 lỗi nặng»
  else summary = fixedCount === null ? 'Máy đã tự sửa cảnh · bước ⑤ Kịch bản' : `Đã sửa ${fixedCount} lỗi · 1 vòng · bước ⑤ Kịch bản`;

  const undoVersionId = state === 'current' && fix?.undoVersionId ? fix.undoVersionId : null;
  const redoVersionId = state === 'undone' && fix?.redoVersionId ? fix.redoVersionId : null;
  return {
    ep: ep.ep, state, preVersionNo: pre, postVersionNo: post, fixedCount, summary,
    steps: state === 'undone' || !receipt ? [] : stepsOf(receipt),
    scenes: state === 'undone' ? [] : scenes,
    pairs, canDiff: pairs.length > 0, canUndo: !!undoVersionId, canRedo: !!redoVersionId,
    undoVersionId, redoVersionId,
    undoLabel: undoVersionId ? `Hoàn tác về v${pre}` : null,
    redoLabel: redoVersionId ? `Dùng lại bản máy sửa (v${post})` : null,
    greyNote: state === 'legacy' ? LEGACY_NOTE : state === 'superseded' ? SUPERSEDED_NOTE : null,
    orangeNote: state === 'undone' ? UNDONE_NOTE : null,
  };
};

// ─── Chữ cảnh + diff theo dòng ────────────────────────────────────────────

const beatLine = (b: ScriptBeat, nameOf: (id?: string) => string): string => {
  if (b.kind === 'dialogue' || b.kind === 'voiceover') {
    const who = b.kind === 'dialogue' && !b.characterId && b.speakerLabel ? b.speakerLabel : nameOf(b.characterId);
    const head = `${who}${b.kind === 'voiceover' ? ' (V.O.)' : ''}${b.cue ? ` (${b.cue})` : ''}`;
    return `${head}: ${b.text}${b.kind === 'dialogue' && b.subtext ? ` · ngầm: ${b.subtext}` : ''}`;
  }
  return b.text;
};

/**
 * Chữ của một cảnh theo từng dòng, thứ tự và nhãn bám `ScriptReader`: hành động của cảnh, rồi từng beat (thoại `Tên (cue): …`, V.O. `Tên (V.O.): …`, hành động/chú thích nguyên văn).
 * `nameOf` tra tên nhân vật (mặc định: trả lại id). Thuần — `ScriptReader` vẽ bằng JSX nên không có hàm dùng chung.
 */
export const formatSceneLines = (scene: ScriptScene | null | undefined, nameOf: (id?: string) => string = (id) => id ?? ''): string[] => {
  if (!scene) return [];
  const out: string[] = [];
  if (typeof scene.action === 'string' && scene.action.trim()) out.push(scene.action.trim());
  (Array.isArray(scene.beats) ? scene.beats : []).forEach((b) => { if (isObj(b) && typeof b.text === 'string') out.push(beatLine(b, nameOf)); });
  return out;
};

export interface DiffLine { kind: 'keep' | 'del' | 'add'; text: string }

/** Diff theo DÒNG (LCS) giữa hai cảnh. Cảnh vắng ⇒ coi như không có dòng nào (toàn thêm / toàn xoá). Không thư viện diff. */
export const sceneDiffLines = (
  before: ScriptScene | null | undefined, after: ScriptScene | null | undefined, nameOf?: (id?: string) => string,
): DiffLine[] => {
  const a = formatSceneLines(before, nameOf);
  const b = formatSceneLines(after, nameOf);
  const n = a.length; const m = b.length;
  const t: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) t[i][j] = a[i] === b[j] ? t[i + 1][j + 1] + 1 : Math.max(t[i + 1][j], t[i][j + 1]);
  const out: DiffLine[] = [];
  let i = 0; let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ kind: 'keep', text: a[i] }); i++; j++; }
    else if (t[i + 1][j] >= t[i][j + 1]) { out.push({ kind: 'del', text: a[i] }); i++; }
    else { out.push({ kind: 'add', text: b[j] }); j++; }
  }
  while (i < n) out.push({ kind: 'del', text: a[i++] });
  while (j < m) out.push({ kind: 'add', text: b[j++] });
  return out;
};

// ─── Hộp xác nhận ─────────────────────────────────────────────────────────

export interface UndoConsequences { criticalBack: number; becomeOld: string[]; unchanged: string[] }

/**
 * Hai danh sách của hộp «Hoàn tác»: «Sẽ thành Cũ / cần làm lại» và «Không đổi».
 * Số CRITICAL trở lại = CRITICAL của lượt chấm ĐẦU trong biên lai lúc sinh (= biên lai của bản trước sửa sẽ được khôi phục). Dòng Chapters chỉ có khi tập đang «Đã đẩy» (`up_to_date`).
 */
export const undoConsequences = (ep: Pick<WorkbenchEpisode, 'ep' | 'handoff' | 'autoFix'>, script: EpisodeScriptRecord | null | undefined): UndoConsequences => {
  const receipt = repairReceiptOf(script);
  const criticalBack = receipt ? firstPassCritical(receipt) : 0;
  const changed = isObj(receipt?.repair) && Array.isArray(receipt!.repair!.changedScenes) ? receipt!.repair!.changedScenes.filter((x): x is string => typeof x === 'string') : [];
  const labels = changed.map((id) => sceneLabel(id, script?.script?.scenes));
  const becomeOld: string[] = [];
  if (criticalBack > 0) becomeOld.push(`${criticalBack} lỗi nặng của tập trở lại: chip giám khảo hiện lại, tập vào “Cần bạn xem”, việc đẩy sẽ bị chặn đến khi sửa`);
  if (labels.length) becomeOld.push(`${labels.join(', ')} quay về chữ trước khi máy sửa`);
  if (ep.handoff === 'up_to_date') becomeOld.push('Chapters: bản đã đẩy sẽ khác bản mới — thành “Cần đẩy lại”');
  const post = ep.autoFix?.postVersionNo;
  const unchanged = [
    `Bản${post ? ` v${post}` : ''} của máy vẫn nằm trong “Phiên bản” — có thể “Dùng lại” sau`,
    'Các tập khác, khung xương, bảng tên',
    'Máy không tự sửa lại tập này nữa',
  ];
  return { criticalBack, becomeOld, unchanged };
};

export interface ConfirmDialog { title: string; lead: string; okText: string; becomeOld: string[]; unchanged: string[] }

export const undoDialogOf = (ep: Pick<WorkbenchEpisode, 'ep' | 'handoff' | 'autoFix'>, script: EpisodeScriptRecord | null | undefined): ConfirmDialog => {
  const pre = ep.autoFix?.preVersionNo; const post = ep.autoFix?.postVersionNo;
  const c = undoConsequences(ep, script);
  return {
    title: `Hoàn tác bản máy sửa của tập ${ep.ep}?`,
    lead: `Tập ${ep.ep} quay về phiên bản v${pre} (trước khi máy sửa). Bản v${post} của máy vẫn nằm trong “Phiên bản”.`,
    okText: `Xác nhận hoàn tác về v${pre}`,
    becomeOld: c.becomeOld, unchanged: c.unchanged,
  };
};

export const redoDialogOf = (ep: Pick<WorkbenchEpisode, 'ep' | 'autoFix'>): ConfirmDialog => {
  const pre = ep.autoFix?.preVersionNo; const post = ep.autoFix?.postVersionNo;
  return {
    title: `Dùng lại bản máy sửa của tập ${ep.ep}?`,
    lead: `Tập ${ep.ep} quay lại phiên bản v${post} do máy sửa. Bản v${pre} (trước khi máy sửa) vẫn nằm trong “Phiên bản”.`,
    okText: `Xác nhận dùng lại v${post}`,
    becomeOld: [], unchanged: [],
  };
};
